import React, { useEffect, useState } from 'react';
import { TreeSelect } from 'antd';
import './nestedAntd.scss';

const { SHOW_CHILD } = TreeSelect;

export type DataNode = {
  title: string;
  value: string;
  key: string;
  children?: DataNode[];
};

type DataOptionsNode = {
  title: JSX.Element | string;
  value: string;
  key: string;
  children?: DataOptionsNode[];
  disableCheckbox?: boolean;
  disabled?: boolean;
};

type NestedSelectProps = {
  name: string;
  label?: string;
  placeholder?: string;
  options: DataOptionsNode[];
  control: any;
  Controller: any;
  errors: any;
  wrapperClass?: string;
  initialValues?: string[];
};

const NestedAntdMultiSelect: React.FC<NestedSelectProps> = ({
  name,
  label,
  placeholder,
  options,
  control,
  Controller,
  errors,
  wrapperClass,
  initialValues
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  useEffect(() => {
    if (initialValues && initialValues.length > 0) {
      setSelectedValues(initialValues);
    }
  }, [initialValues]);

  // Collect only child node values
  const allValueKeys = options.flatMap((option) =>
    option.children ? option.children.map((child) => child.value) : []
  );

  // Conditional titles based on available options
  const noOptionsAvailable = options.length === 0; // Check if there are any options
  const allSelected = selectedValues.length === allValueKeys.length && allValueKeys.length > 0;

  const treeDataWithSelectAll = noOptionsAvailable
    ? []
    : [
        {
          title: allSelected ? (
            <span style={{ color: '#1890ff', cursor: 'pointer' }}>Unselect All</span>
          ) : (
            <span style={{ color: '#1890ff', cursor: 'pointer' }}>Select All</span>
          ),
          value: 'selectAll',
          key: 'selectAll',
          checkable: false
        },
        ...options
      ];

  return (
    <div className={`${wrapperClass} flex flex-col items-start pb-4 text-sm`}>
      {label && (
        <label className="text-neutral9 font-semibold" htmlFor={name}>
          {label}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange } }: any) => (
          <TreeSelect
            style={{
              marginTop: '0.25rem',
              width: '100%',
              borderRadius: '0.5rem'
            }}
            className="custom-tree-select"
            treeData={treeDataWithSelectAll}
            treeCheckable={true}
            showCheckedStrategy={SHOW_CHILD}
            placeholder={placeholder || 'Select Doors'}
            value={selectedValues}
            onChange={(newValues) => {
              setSelectedValues(newValues);
              onChange(newValues); // Update form state
            }}
            maxTagCount={5}
            maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length} Doors ...`}
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              const target = event.target as HTMLElement; // type assertion
              const isSelectAll = target.textContent && target.textContent.includes('Select All');
              const isUnselectAll =
                target.textContent && target.textContent.includes('Unselect All');

              if (isSelectAll) {
                setSelectedValues(allValueKeys);
                onChange(allValueKeys);
              } else if (isUnselectAll) {
                setSelectedValues([]);
                onChange([]);
              }
            }}
            filterTreeNode={(input, node) => {
              const title = node.title;
              if (typeof title === 'string') {
                return title.toLowerCase().includes(input.toLowerCase());
              } else if (React.isValidElement(title) && typeof title.props.children === 'string') {
                return title.props.children.toLowerCase().includes(input.toLowerCase());
              }
              return false;
            }}
          />
        )}
      />
      {errors[name] && (
        <span className="error" role="alert">
          {errors[name].message}
        </span>
      )}
    </div>
  );
};

export default NestedAntdMultiSelect;
